import { KitProductModel } from "../../Api/Models/product.model";
import { StockModel } from "../../Api/Models/stock.model";
import { t } from "i18next";
import { CheckIcon } from "../Icons/CheckIcon";
import React from "react";
import { ExchangeMissingIcon } from "../Icons/ExchangeMissingIcon";
import Popover from "../Popover";
import { RiderProductModel } from "../../Api/Models/rider.model";
import { MultiExchangeItem } from "./Exchange";
import { isStockAvailable } from "./stockAvailability";

interface ExchangeMissingProps {
    missingProducts: KitProductModel[];
    stocks: StockModel[];
    setMissingProducts: React.Dispatch<React.SetStateAction<KitProductModel[]>>;
    boxKeys: { [kitProductId: number]: string };
    openBoxKeyModal: (kitProductId: number) => void;

    selectedGears: MultiExchangeItem[];
    gears: RiderProductModel[];

    wouldExceedCategoryLimit: (
        categoryId?: number | null,
        max?: number | null,
        addQty?: number
    ) => boolean;

    getRemainingInCategory: (
        categoryId?: number | null,
        max?: number | null
    ) => number | null;
}

const ExchangeMissing: React.FunctionComponent<ExchangeMissingProps> = ({
                                                                            missingProducts,
                                                                            stocks,
                                                                            setMissingProducts,
                                                                            boxKeys,
                                                                            openBoxKeyModal,
                                                                            selectedGears,
                                                                            gears,
                                                                            wouldExceedCategoryLimit,
                                                                            getRemainingInCategory,
                                                                        }) => {
    const stockAvailable = (
        productId: number,
        variationId: number | null,
        quantity: number
    ): boolean => isStockAvailable(stocks, productId, variationId, quantity);

    const canSelectMissingProduct = (kitProduct: KitProductModel): boolean => {
        if (kitProduct.checked) return true;

        return !wouldExceedCategoryLimit(
            kitProduct.categoryId,
            kitProduct.categoryMaxProducts,
            Math.max(kitProduct.quantity, 1)
        );
    };

    const handleToggleMissing = (kitProduct: KitProductModel) => {
        if (kitProduct.checked) {
            setMissingProducts((prev) =>
                prev.map((item) =>
                    item.id === kitProduct.id
                        ? {
                            ...item,
                            checked: false,
                            selectedVariationId: -1,
                        }
                        : item
                )
            );
            return;
        }

        const wouldExceed = wouldExceedCategoryLimit(
            kitProduct.categoryId,
            kitProduct.categoryMaxProducts,
            Math.max(kitProduct.quantity, 1)
        );

        if (wouldExceed) {
            alert(
                t("Maximum {{max}} products allowed from this category", {
                    max: kitProduct.categoryMaxProducts,
                })
            );
            return;
        }

        setMissingProducts((prev) =>
            prev.map((item) =>
                item.id === kitProduct.id
                    ? {
                        ...item,
                        checked: true,
                        selectedVariationId:
                            item.selectedVariationId !== null &&
                            item.selectedVariationId !== undefined
                                ? item.selectedVariationId
                                : -1,
                    }
                    : item
            )
        );
    };

    return (
        <>
            {missingProducts
                .filter((x, index, array) => {
                    if (!x.optionalGroupId) return true;

                    if (
                        array.some(
                            (p) => p.optionalGroupId === x.optionalGroupId && p.selected
                        )
                    ) {
                        return x.selected;
                    }

                    return (
                        array.findIndex(
                            (group) => group.optionalGroupId === x.optionalGroupId
                        ) === index
                    );
                })
                .map((kitProduct) => {
                    const limitReached = wouldExceedCategoryLimit(
                        kitProduct.categoryId,
                        kitProduct.categoryMaxProducts,
                        Math.max(kitProduct.quantity, 1)
                    );

                    const remaining = getRemainingInCategory(
                        kitProduct.categoryId,
                        kitProduct.categoryMaxProducts
                    );

                    const selectable = canSelectMissingProduct(kitProduct);

                    if (
                        kitProduct.optionalGroupId &&
                        !missingProducts.some(
                            (x) =>
                                x.optionalGroupId === kitProduct.optionalGroupId && x.selected
                        )
                    ) {
                        const availableGroupOptions = missingProducts
                            .filter((x) => x.optionalGroupId === kitProduct.optionalGroupId)
                            .filter((x) => stockAvailable(x.productId, null, x.quantity))
                            .filter((x) => !wouldExceedCategoryLimit(x.categoryId, x.categoryMaxProducts, Math.max(x.quantity, 1)));

                        const hasNoGroupOptions = availableGroupOptions.length === 0;

                        return (
                            <div
                                className={`product-list-card mb-4 ${
                                    kitProduct.checked ? "marked" : ""
                                }`}
                                key={kitProduct.id}
                            >
                                <div className="relative">
                                    <div className="flex items-center gap-2">
                                        <div className="relative">
                                            <div className="product-list-card__image">
                                                <img
                                                    src={kitProduct.image || "https://via.placeholder.com/60"}
                                                    alt="product"
                                                />
                                            </div>
                                        </div>

                                        <div className="w-full">
                                            <div className="product-list-card__title">
                                                <div>{kitProduct.name}</div>
                                                {hasNoGroupOptions ? (
                                                    <span className="text-red">{t("Out of stock")}</span>
                                                ) : (
                                                    <select
                                                        className={`input-field input-field--small input-field--auto-width ${
                                                            !kitProduct.checked ? "text-gray" : "text-blue"
                                                        }`}
                                                        onChange={(e) => {
                                                            const selectedId = parseInt(e.target.value, 10);

                                                            setMissingProducts((prev) =>
                                                                prev.map((item) => {
                                                                    if (
                                                                        item.optionalGroupId === kitProduct.optionalGroupId
                                                                    ) {
                                                                        item.selected = false;
                                                                        item.checked = false;
                                                                    }

                                                                    if (item.id === selectedId) {
                                                                        const next = { ...item };
                                                                        next.selected = true;
                                                                        next.checked = true;

                                                                        if (next.variations?.length > 0) {
                                                                            const firstAvailable =
                                                                                next.variations.find((v) =>
                                                                                    stockAvailable(
                                                                                        next.productId,
                                                                                        v.id,
                                                                                        next.quantity
                                                                                    )
                                                                                )?.id ?? -1;

                                                                            next.selectedVariationId = firstAvailable;
                                                                        } else {
                                                                            next.selectedVariationId = -1;
                                                                        }

                                                                        return next;
                                                                    }

                                                                    return item;
                                                                })
                                                            );
                                                        }}
                                                    >
                                                        <option>- Please Select -</option>
                                                        {availableGroupOptions.map((groupProduct) => (
                                                            <option key={groupProduct.id} value={groupProduct.id}>
                                                                {groupProduct.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex gap-2 items-center">
                                            {!hasNoGroupOptions && (
                                                <button
                                                    type="button"
                                                    className={`product-list-card__check ${
                                                        kitProduct.checked ? "checked" : "needtocheck"
                                                    } ${
                                                        !selectable ? "opacity-50 cursor-not-allowed" : ""
                                                    }`}
                                                    onClick={() => handleToggleMissing(kitProduct)}
                                                >
                                                    {kitProduct.checked && <CheckIcon />}
                                                </button>
                                            )}

                                            <div className="exchange-list-card__exchange-type-icon-holder">
                                                <Popover
                                                    defaultPosition={"left"}
                                                    text={t("Missing product")}
                                                >
                                                    <div className="exchange-list-card__exchange-type-icon">
                                                        <ExchangeMissingIcon />
                                                    </div>
                                                </Popover>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    }

                    return (
                        <div
                            className={`product-list-card mb-4 ${
                                kitProduct.checked ? "marked" : ""
                            } ${!selectable ? "opacity-50" : ""}`}
                            key={kitProduct.id}
                        >
                            <div className="relative">
                                <div className="flex items-center gap-2">
                                    <div className="relative">
                                        <div className="product-list-card__image">
                                            <img
                                                src={kitProduct.image || "https://via.placeholder.com/60"}
                                                alt="product"
                                            />
                                        </div>
                                    </div>

                                    <div className="w-full">
                                        <div className="product-list-card__title">
                                            {kitProduct.name}
                                            {kitProduct.quantity > 1 && ` (${kitProduct.quantity})`}

                                            <div className="text-xs text-gray-500 mt-1">
                                                Category: {" "} {kitProduct.categoryName ?? "N/A"} |
                                                Max:{" "} {kitProduct.categoryMaxProducts ? kitProduct.categoryMaxProducts : "No limit"}
                                            </div>

                                            {remaining !== null &&
                                                remaining === 1 &&
                                                !kitProduct.checked && (
                                                    <div className="text-xs text-orange-500 mt-1">
                                                        {t("Only {{count}} items remaining in this category", {
                                                            count: remaining,
                                                        })}
                                                    </div>
                                                )}

                                            {limitReached && !kitProduct.checked && (
                                                <div className="text-xs text-red-500 mt-1">
                                                    {t("Category limit reached")}
                                                </div>
                                            )}

                                            {kitProduct.box && boxKeys[kitProduct.id] && (
                                                <div className="font-12 color-secondary">
                                                    {t("Reference")}: {boxKeys[kitProduct.id]}
                                                </div>
                                            )}

                                            {kitProduct.checked &&
                                                kitProduct.box &&
                                                stockAvailable(kitProduct.productId, null, kitProduct.quantity) && (
                                                    <button
                                                        type="button"
                                                        className="button button--small button--light"
                                                        onClick={() => openBoxKeyModal(kitProduct.id)}
                                                    >
                                                        {boxKeys[kitProduct.id]
                                                            ? t("Edit reference")
                                                            : t("Set reference")}
                                                    </button>
                                                )}
                                        </div>

                                        {!stockAvailable(
                                            kitProduct.productId,
                                            null,
                                            kitProduct.quantity
                                        ) ? (
                                            <span className="text-red">{t("Out of stock")}</span>
                                        ) : (
                                            <>
                                                {kitProduct.optionalGroupId && (
                                                    <span
                                                        className="text-blue cursor-pointer"
                                                        onClick={() => {
                                                            setMissingProducts((prev) =>
                                                                prev.map((item) => {
                                                                    if (
                                                                        item.optionalGroupId === kitProduct.optionalGroupId
                                                                    ) {
                                                                        return {
                                                                            ...item,
                                                                            selected: false,
                                                                            checked: false,
                                                                            selectedVariationId: -1,
                                                                        };
                                                                    }
                                                                    return item;
                                                                })
                                                            );
                                                        }}
                                                    >
                                                        {t("<< Select an other product")}
                                                    </span>
                                                )}
                                            </>
                                        )}

                                        <div className="exchange-list-card__exchange-type-icon-holder">
                                            <Popover
                                                defaultPosition={"left"}
                                                text={t("Missing Product")}
                                            >
                                                <div className="exchange-list-card__exchange-type-icon">
                                                    <ExchangeMissingIcon />
                                                </div>
                                            </Popover>
                                        </div>
                                    </div>

                                    {stockAvailable(kitProduct.productId, null, kitProduct.quantity) && (
                                        <div className="flex gap-2 items-center">
                                            {kitProduct.variations.length > 0 && (
                                                <select
                                                    className="input-field input-field--small input-field--variation-width"
                                                    value={kitProduct.selectedVariationId ?? -1}
                                                    disabled={!kitProduct.checked}
                                                    onChange={(e) => {
                                                        const selectedVariationId = parseInt(e.target.value, 10);

                                                        setMissingProducts((prev) =>
                                                            prev.map((item) =>
                                                                item.id === kitProduct.id
                                                                    ? {
                                                                        ...item,
                                                                        selectedVariationId,
                                                                        checked: true,
                                                                    }
                                                                    : item
                                                            )
                                                        );
                                                    }}
                                                >
                                                    <option value={-1} disabled>
                                                        -
                                                    </option>
                                                    {kitProduct.variations
                                                        .filter((v) =>
                                                            stockAvailable(
                                                                kitProduct.productId,
                                                                v.id,
                                                                kitProduct.quantity
                                                            )
                                                        )
                                                        .map((v) => (
                                                            <option value={v.id} key={v.id}>
                                                                {v.name}
                                                            </option>
                                                        ))}
                                                </select>
                                            )}

                                            <button
                                                type="button"
                                                className={`product-list-card__check ${
                                                    kitProduct.checked ? "checked" : "needtocheck"
                                                } ${!selectable ? "opacity-50 cursor-not-allowed" : ""}`}
                                                onClick={() => handleToggleMissing(kitProduct)}
                                                disabled={remaining!==null && remaining<=0 && !kitProduct.checked}
                                            >
                                                {kitProduct.checked && <CheckIcon />}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
        </>
    );
};

export default ExchangeMissing;
